import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';
import YouTubePlayer from '../components/YouTubePlayer';

export default function TechVideosPage() {
  const { user, csrfToken } = useAuth();

  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pageSize, setPageSize] = useState(4);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedRatings, setSelectedRatings] = useState({});
  const [error, setError] = useState(null);

  // بارگذاری اولیه
  useEffect(() => {
    (async () => {
      try {
        const [videosRes, catsRes] = await Promise.all([
          axios.get('/api/videos', { withCredentials: true }),
          axios.get('/api/video-categories', { withCredentials: true }),
        ]);
        setVideos(videosRes.data);
        setCategories(catsRes.data);
      } catch {
        setError('Error fetching videos or categories');
      }
    })();
  }, []);

  // ثبت ریتینگ
  const handleRating = async (videoId, value) => {
    if (!user) return;

    const uid = String(user._id);
    const numericValue = Number(value);

    setSelectedRatings(prev => ({ ...prev, [videoId]: numericValue }));

    setVideos(prev =>
      prev.map(v => {
        if (v._id !== videoId) return v;
        const filtered = v.ratings.filter(r => String(r.userId) !== uid);
        return { ...v, ratings: [...filtered, { userId: uid, value: numericValue }] };
      })
    );

    try {
      const { data } = await axios.post(
        `/api/videos/${videoId}/rating`,
        { value: numericValue },
        { headers: { 'X-CSRF-Token': csrfToken }, withCredentials: true }
      );
      setVideos(prev => prev.map(v => (v._id === data._id ? data : v)));
    } catch {
      setError('Error submitting rating');
    }
  };

  // ثبت کامنت
  const handleComment = async (videoId, text) => {
    try {
      const { data } = await axios.post(
        `/api/videos/${videoId}/comment`,
        { text },
        { headers: { 'X-CSRF-Token': csrfToken }, withCredentials: true }
      );
      setVideos(prev => prev.map(v => (v._id === data._id ? data : v)));
    } catch {
      setError('Error submitting comment');
    }
  };

  // میانگین ریتینگ
  const computeAvg = (ratings) => {
    if (!ratings || ratings.length === 0) return '0.0';
    const byUser = new Map();
    for (const r of ratings) byUser.set(String(r.userId), Number(r.value));
    const values = Array.from(byUser.values());
    const sum = values.reduce((s, v) => s + (isNaN(v) ? 0 : v), 0);
    return (sum / values.length).toFixed(1);
  };

  // فیلتر + مرتب‌سازی
  const filteredVideos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return [...videos]
      .filter(v => (term ? v.title.toLowerCase().includes(term) : true))
      .filter(v => (categoryFilter ? v.category === categoryFilter : true))
      .sort((a, b) => {
        let result;
        if (sortBy === 'title') {
          result = a.title.localeCompare(b.title);
        } else {
          const ad = new Date(a.publishDate || a.createdAt || 0).getTime();
          const bd = new Date(b.publishDate || b.createdAt || 0).getTime();
          result = ad - bd;
        }
        return sortOrder === 'asc' ? result : -result;
      });
  }, [videos, searchTerm, categoryFilter, sortBy, sortOrder]);

  // صفحه‌بندی
  const totalPages = Math.ceil(filteredVideos.length / pageSize);
  const paginatedVideos = filteredVideos.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, sortBy, sortOrder, pageSize]);

  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container py-5">
      <h1 className="h4 mb-4">Educational Videos</h1>

      {/* کنترل‌ها */}
      <div className="mb-4 row g-3">
        <div className="col-md-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <select
            className="form-select"
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map(cat => (
              <option key={cat._id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <select
            className="form-select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="date">Sort by date</option>
            <option value="title">Sort by title</option>
          </select>
        </div>

        <div className="col-md-3">
          <select
            className="form-select"
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value)}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
      </div>

      {/* تعداد ویدئو */}
      <div className="mb-3">
        <div className="alert alert-info text-center fs-5">
          🎬 <strong>{filteredVideos.length}</strong> videos found based on your filters
        </div>
      </div>

      {/* لیست ویدئوها */}
      <div className="row g-4">
        {paginatedVideos.map(v => {
          const uid = user ? String(user._id) : null;
          const userRating =
            selectedRatings[v._id] ??
            (v.ratings.find(r => String(r.userId) === uid)?.value ?? 0);
          const avgRating = computeAvg(v.ratings);
          const categoryName = v.category || 'Uncategorized';

          return (
            <div key={v._id} className="col-md-6">
              <div className="card shadow-sm">

                {/* پلیر جدید */}
                <YouTubePlayer url={v.youtubeUrl} title={v.title} />

                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <h5 className="mb-2">{v.title}</h5>
                    <span className="badge bg-secondary">{categoryName}</span>
                  </div>

                  <p className="mb-2">{v.description}</p>

                  <div className="d-flex gap-3 text-muted mb-3">
                    <span>Avg rating: {avgRating} ★</span>
                    <span>
                      Uploaded: {new Date(v.publishDate || v.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  {user && (
                    <div className="mb-3">
                      <label className="form-label">Your rating:</label>
                      <div>
                        {[1, 2, 3, 4, 5].map(val => (
                          <span
                            key={val}
                            style={{
                              cursor: 'pointer',
                              fontSize: '1.5rem',
                              color: Number(userRating) >= val ? 'gold' : 'gray'
                            }}
                            onClick={() => handleRating(v._id, val)}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {user && (
                    <form
                      onSubmit={e => {
                        e.preventDefault();
                        const text = e.target.comment.value;
                        if (text.trim()) handleComment(v._id, text);
                        e.target.reset();
                      }}
                    >
                      <div className="mb-3">
                        <label className="form-label">Leave a comment:</label>
                        <textarea name="comment" className="form-control" rows="2" required />
                      </div>
                      <button className="btn btn-primary btn-sm">Submit</button>
                    </form>
                  )}

                  <ul className="list-group list-group-flush mt-3">
                    {v.comments.map(c => (
                      <li key={c._id || `${c.userId}-${c.createdAt}`} className="list-group-item">
                        <strong>{c.userName}:</strong> {c.text}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}

        {paginatedVideos.length === 0 && (
          <p className="text-muted">No videos available</p>
        )}
      </div>

      {/* صفحه‌بندی */}
      <div className="mt-3">
        <nav>
          <ul className="pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <li
                key={page}
                className={`page-item ${page === currentPage ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}
